/**
 * Feedback Page (admin-only)
 *
 * Lists all feedback submitted across the app — both parent dashboard and
 * kids mode. Visible only to admins (gated server-side by ADMIN_EMAILS and
 * in the dashboard nav by user.is_admin).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Star, MessageCircleHeart } from 'lucide-react';
import { getAllFeedback, type AdminFeedback } from '@/services/feedback';
import { LoadingSpinner } from '@/components/shared';

type Filter = 'all' | 'parent' | 'kids';

function Stars({ rating }: { rating: number | null }) {
  if (!rating) return <span className="text-[13px]" style={{ color: '#9B978E' }}>No rating</span>;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((v) => (
        <Star
          key={v}
          className="h-3.5 w-3.5"
          style={{ color: '#E8B630' }}
          fill={v <= rating ? '#E8B630' : 'none'}
        />
      ))}
    </span>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function FeedbackPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const { data: feedback = [], isLoading, isError } = useQuery({
    queryKey: ['feedback', 'all'],
    queryFn: getAllFeedback,
  });

  const filtered = useMemo<AdminFeedback[]>(
    () => (filter === 'all' ? feedback : feedback.filter((f) => f.source === filter)),
    [feedback, filter]
  );

  const counts = useMemo(
    () => ({
      all: feedback.length,
      parent: feedback.filter((f) => f.source === 'parent').length,
      kids: feedback.filter((f) => f.source === 'kids').length,
    }),
    [feedback]
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <MessageCircleHeart className="h-6 w-6" style={{ color: '#3ECDC6' }} />
        <h1 className="text-2xl font-semibold" style={{ color: '#2A2926' }}>Feedback</h1>
      </div>
      <p className="text-[14px] mb-6" style={{ color: '#6B675E' }}>
        Everything submitted from the parent dashboard and kids mode.
      </p>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-5">
        {(['all', 'parent', 'kids'] as Filter[]).map((f) => {
          const active = filter === f;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="px-4 py-2 rounded-full text-[13.5px] font-medium capitalize transition-colors"
              style={{
                background: active ? '#3ECDC6' : '#FFFFFF',
                color: active ? '#FFFFFF' : '#6B675E',
                border: `1px solid ${active ? '#3ECDC6' : '#E8E6E1'}`,
              }}
            >
              {f} ({counts[f]})
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><LoadingSpinner /></div>
      ) : isError ? (
        <div className="py-16 text-center text-[14px]" style={{ color: '#C0524B' }}>
          Could not load feedback. Please try again.
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-[14px]" style={{ color: '#9B978E' }}>
          No feedback yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((f) => (
            <div
              key={f.id}
              className="rounded-xl border p-4"
              style={{ background: '#FFFFFF', borderColor: '#E8E6E1' }}
            >
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <span
                    className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide"
                    style={{
                      background: f.source === 'kids' ? '#E8F6F4' : '#FDF6E8',
                      color: f.source === 'kids' ? '#2A9D96' : '#B8860B',
                    }}
                  >
                    {f.source}
                  </span>
                  <Stars rating={f.rating} />
                </div>
                <span className="text-[12px]" style={{ color: '#9B978E' }}>{formatDate(f.created_at)}</span>
              </div>

              <p className="text-[14.5px] whitespace-pre-wrap mb-2" style={{ color: '#2A2926' }}>
                {f.message}
              </p>

              <div className="flex items-center gap-3 text-[12px]" style={{ color: '#9B978E' }}>
                <span>{f.user_email}</span>
                {f.child_name && <span>· child: {f.child_name}</span>}
                {f.page && <span>· {f.page}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
